/*
 * @providesModule AssetHelper
 *
 */

import {SkillzStyle} from '@sdk-mobile';
import Preferences from '@/sdk-mobile-web/Preferences/Preferences';

const OTA_fix_string_literal = '2024.0.388';

// temporary fix to handle version as re routing needs to be handled in root page
function modifyBaseThemeUrl(url) {
  if (!url) {
    return;
  }
  const lastSlashIndex = url.lastIndexOf('/');
  if (lastSlashIndex <= 0) return url;
  const newUrl = url.slice(0, lastSlashIndex + 1) + OTA_fix_string_literal;
  return newUrl;
}

/**
 * Validates that a URL is safe to use in the current environment.
 * Rejects localhost URLs when running in non-local environments to prevent
 * CORS errors and security issues.
 */
function validateUrlForEnvironment(url: string | undefined): string | undefined {
  if (!url) {
    return url;
  }

  // Check if we're running in a browser environment
  if (typeof window === 'undefined') {
    return url;
  }

  // Check if the URL is a localhost URL
  const isLocalhost =
    /^https?:\/\/localhost(:\d+)?/.test(url) ||
    /^https?:\/\/127\.0\.0\.1(:\d+)?/.test(url) ||
    /^https?:\/\/0\.0\.0\.0(:\d+)?/.test(url);

  if (!isLocalhost) {
    return url;
  }

  // Check if the current page is also on localhost
  const currentHost = window.location.hostname;
  const isPageLocalhost =
    currentHost === 'localhost' ||
    currentHost === '127.0.0.1' ||
    currentHost === '0.0.0.0' ||
    currentHost === '';

  if (isPageLocalhost) {
    // Allow localhost URLs when page is also on localhost
    return url;
  }

  // Reject localhost URLs when running on remote host (CORS violation)
  console.error(
    `[AssetHelper] Rejected localhost URL "${url}" when running on remote host "${currentHost}". ` +
      `This would cause CORS errors. Please check your theme.base.url configuration.`
  );
  return undefined;
}

interface AssetHelperType {
  isURL(assetName: string): boolean;
  pathForAssetNamed(assetName: string, isBundled: boolean): string;
  pathForLocalImage(imageName: string): string;
  urlForRemoteSourceImage(imageName: string): string;
  urlForRemoteThemeImage(imageName: string): string;
  _urlForRemoteSourceImage(imageName: string): string;
  _urlForRemoteThemeImage(imageName: string): string;
  preloadImages(): void;
  pathForSkillzUIPackage(sourceBaseUrl: string): string;
}

const AssetHelper: AssetHelperType = {
  isURL(assetName) {
    return (
      assetName.startsWith('http://') ||
      assetName.startsWith('https://') ||
      assetName.startsWith('ftp://') ||
      assetName.startsWith('file://') ||
      assetName.startsWith('content://') ||
      assetName.includes('skillz-generic-avatar')
    );
  },

  pathForAssetNamed(assetName, isBundled) {
    if (this.isURL(assetName)) {
      return assetName;
    } else if (isBundled) {
      return this.pathForLocalImage(assetName);
    } else if (SkillzStyle.isThemeImage(assetName)) {
      return this.urlForRemoteThemeImage(assetName);
    }
    return this.urlForRemoteSourceImage(assetName);
  },

  pathForLocalImage(imageName) {
    return `assets/${imageName}`;
  },

  urlForRemoteSourceImage(imageName) {
    // TODO (websdk-post-mvp): Replace with some ENV variable
    // Validate imageName to prevent directory path requests
    if (!imageName || imageName.trim() === '' || imageName === '/') {
      console.warn(
        '[AssetHelper] Empty or invalid imageName provided for source image:',
        imageName
      );
      return '';
    }

    const baseThemeUrl = Preferences.get('theme.base.url');
    const validatedUrl = validateUrlForEnvironment(baseThemeUrl);
    if (!validatedUrl) {
      console.warn(
        `[AssetHelper] Cannot construct URL for source image "${imageName}" - invalid base URL`
      );
      return '';
    }
    const modifiedBaseUrl = modifyBaseThemeUrl(validatedUrl);
    return `${modifiedBaseUrl}/assets/source_images/${imageName}`;
  },

  _urlForRemoteSourceImage(imageName) {
    // TODO (websdk-post-mvp): Replace with some ENV variable
    // Validate imageName to prevent directory path requests
    if (!imageName || imageName.trim() === '' || imageName === '/') {
      console.warn(
        '[AssetHelper] Empty or invalid imageName provided for source image:',
        imageName
      );
      return '';
    }

    const baseThemeUrl = Preferences.get('theme.base.url');
    const validatedUrl = validateUrlForEnvironment(baseThemeUrl);
    if (!validatedUrl) {
      console.warn(
        `[AssetHelper] Cannot construct URL for source image "${imageName}" - invalid base URL`
      );
      return '';
    }
    const modifiedBaseUrl = modifyBaseThemeUrl(validatedUrl);
    return `${modifiedBaseUrl}/assets/source_images/${imageName}`;
  },

  /**
   * Returns the packaged URL path for the Skillz UI repo. This is passed to the Skillz UI repo via AssetsProvider so that all environments can access the images in the specific Skillz UI repo S3 bucket.
   */
  pathForSkillzUIPackage(sourceBaseUrl: string): string {
    const baseUrl = sourceBaseUrl.replace('/assets/source_images/', '');
    const skillzUIPackageUrl = `${baseUrl}/assets/node_modules/@skillz/ui/src/assets/images/`;
    return skillzUIPackageUrl;
  },

  urlForRemoteThemeImage(imageName) {
    //// to do here
    // TODO (websdk-post-mvp): This root path is technically incorrect, it should instead come from
    // the ota_version_config. This is a temporary solution until we implement the ota_version_config fetch.
    // Validate imageName to prevent directory path requests
    if (!imageName || imageName.trim() === '' || imageName === '/') {
      console.warn('[AssetHelper] Empty or invalid imageName provided for theme image:', imageName);
      return '';
    }

    const baseThemeUrl = Preferences.get('theme.base.url');
    const customThemeUrl = Preferences.get('theme.custom.url');

    // Validate both URLs, prefer customThemeUrl if valid, otherwise fall back to baseThemeUrl
    const validatedCustomUrl = validateUrlForEnvironment(customThemeUrl);
    const validatedBaseUrl = validateUrlForEnvironment(baseThemeUrl);
    const rootPath = validatedCustomUrl || validatedBaseUrl;

    if (!rootPath) {
      console.warn(
        `[AssetHelper] Cannot construct URL for theme image "${imageName}" - invalid base/custom URLs`
      );
      return '';
    }

    const modifiedBaseUrl = modifyBaseThemeUrl(rootPath);
    return `${modifiedBaseUrl}/assets/theme_images/${imageName}`;
  },

  _urlForRemoteThemeImage(imageName) {
    // TODO (websdk-post-mvp): This root path is technically incorrect, it should instead come from
    // the ota_version_config. This is a temporary solution until we implement the ota_version_config fetch.
    // Validate imageName to prevent directory path requests
    if (!imageName || imageName.trim() === '' || imageName === '/') {
      console.warn('[AssetHelper] Empty or invalid imageName provided for theme image:', imageName);
      return '';
    }

    const baseThemeUrl = Preferences.get('theme.base.url');
    const customThemeUrl = Preferences.get('theme.custom.url');

    // Validate both URLs, prefer customThemeUrl if valid, otherwise fall back to baseThemeUrl
    const validatedCustomUrl = validateUrlForEnvironment(customThemeUrl);
    const validatedBaseUrl = validateUrlForEnvironment(baseThemeUrl);
    const rootPath = validatedCustomUrl || validatedBaseUrl;

    if (!rootPath) {
      console.warn(
        `[AssetHelper] Cannot construct URL for theme image "${imageName}" - invalid base/custom URLs`
      );
      return '';
    }

    const modifiedBaseUrl = modifyBaseThemeUrl(rootPath);
    return `${modifiedBaseUrl}/assets/theme_images/${imageName}`;
  },

  preloadImages() {
    const images = [
      'ic_match_list_header_event_skz.png',
      'ic_cell_signal_1.png',
      'ic_cell_signal_2.png',
      'ic_wifi_signal_2.png',
      'ic_wifi_signal_3.png',
      'ic_account_change_password_skz.png',
      'ic_account_switch_accounts_skz.png',
      'ic_account_login_skz.png',
      'ic_account_withdraw_skz.png',
      'ic_account_change_username_skz.png',
      'ic_account_match_notifications_skz.png',
      'ic_account_audio_skz.png',
      'ic_account_profile_skz.png',
      'ic_account_balance_skz.png',
      'ic_account_logout_skz.png',
      'ic_badge_count_skz.png',
      'ic_account_phone_skz.png',
      'ic_cash_skz.png',
      'QuartzBG.png',
      'SapphireBG.png',
      'EmeraldBG.png',
      'PearlBG.png',
      'RubyBG.png',
      'ObsidianBG.png',
      'emerald_backAvatar1.png',
      'obsidian_backAvatar1.png',
      'pearl_backAvatar1.png',
      'ruby_backAvatar1.png',
      'sapphire_backAvatar1.png',
      'avatar_pending_generic_skz.png',
      'referral_gift.png',
    ];
    images.forEach((imageName) => {
      const img = new Image();
      img.src = this.pathForAssetNamed(imageName, false);
    });
  },
};

export default AssetHelper;
